import { useState, useEffect } from 'react';
//import { generateParams } from './jsonData';
//import { fetchFeed } from './feed-service';

function useFetchPostComplex(url, params, complexParams, skip = false) {
  const [data, setData] = useState(null);
  const [complex] = useState(complexParams || {});

  useEffect(() => {
    if (skip) return;

    // POST to `url` with `params`. Returns parsed JSON / raw text.
    // If an empty response or error occurs, will retry once using
    // `endPointUrlDNS` (if provided) as a fallback.
    async function getData(url, params, triedFallback = false) {
      // Normalise params into x-www-form-urlencoded string.
      let encodedBody;
      if (params instanceof FormData) {
        // Convert FormData -> plain object -> URLSearchParams string
        const obj = {};
        for (let [key, value] of params.entries()) {
          obj[key] = value;
        }
        encodedBody = new URLSearchParams(obj).toString();
      } else if (typeof params === 'object') {
        encodedBody = new URLSearchParams(params).toString();
      } else {
        encodedBody = params;
      }
      //console.log('FETCH URL:', url);
      //console.log('FETCH BODY:', encodedBody);
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
          },
          body: encodedBody
        });
        if (!response.ok) console.warn('FETCH STATUS:', response.status);
        /*
				console.log(
					'FETCH RESPONSE HEADERS:',
					Object.fromEntries(response.headers.entries())
				);
				*/
        const text = await response.text();
        //console.log('FETCH RAW RESPONSE:', text);
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = text;
        }

        const isEmptyObject =
          data && typeof data === 'object' && Object.keys(data).length === 0;
        const isNoData = !data || isEmptyObject;

        // If no meaningful data, try DNS fallback once
        if (isNoData && !triedFallback && complex?.endPointUrlDNS) {
          try {
            return await getData(complex.endPointUrlDNS, params, true);
          } catch (e) {
            // ignore and fall through to return original empty data
          }
        }

        return data;
      } catch (err) {
        console.error('FETCH ERROR:', err);

        // On network/error, try DNS fallback once
        if (!triedFallback && complex?.endPointUrlDNS) {
          try {
            return await getData(complex.endPointUrlDNS, params, true);
          } catch (e) {
            console.error('FETCH ERROR (DNS):', e);
          }
        }
        throw err;
      }
    }

    // Perform fetch and update hook state while preventing updates after unmount.
    let isMounted = true;
    async function perform() {
      try {
        const result = await getData(url, params);
        if (isMounted) setData(result);

        /*
        // If this is a content query, continue as before (operate on fetched `result`)
        if (result?.data?.content?.content) {
          // Create a deep copy of the fetched result so updates don't mutate original
          const updated = JSON.parse(JSON.stringify(result));

          // Define recursive translation function
          const recursiveFeedTranslation = async (obj, keys = ['feed']) => {
            for (var index in obj) {
              if (keys.includes(index)) {
                // Generate feed params based on the feed id
                const feedFetch = generateParams(obj[index], '0', 'feed');

                // Fetch the feed data using the centralized service to dedupe
                let feedData;
                try {
                  feedData = await fetchFeed(obj[index]);
                } catch (err) {
                  // Fallback to local fetch if service fails for any reason
                  try {
                    feedData = await getData(
                      feedFetch.endPointUrl,
                      feedFetch.endPointParams
                    );
                  } catch (err2) {
                    if (feedFetch.endPointUrlDNS) {
                      feedData = await getData(
                        feedFetch.endPointUrlDNS,
                        feedFetch.endPointParams,
                        true
                      );
                    } else {
                      throw err2;
                    }
                  }
                }

                // Replace the feed id with the feed data
                obj[index] = feedData;
              } else if (typeof obj[index] === 'object') {
                // If the item is an object, recursively search through it
                await recursiveFeedTranslation(obj[index], keys);
              }
            }

            // Return the object
            return obj;
          };

          // Create a promise so we can await updated data
          const translatedData = await Promise.all(
            //  Map over each item on the page
            updated.data.content.content.map(async (item) => {
              // If the complex fetch we are doing is to get feed data & the item has a feed id
              if (complex.hasOwnProperty('feeds')) {
                var feedTranslation = null;

                if (
                  complex.feeds instanceof Object &&
                  complex.feeds.hasOwnProperty('keys')
                ) {
                  feedTranslation = await recursiveFeedTranslation(
                    item.data,
                    complex.feeds.keys
                  );
                } else {
                  feedTranslation = await recursiveFeedTranslation(item.data);
                }
                item.data = feedTranslation;
              }

              // Return the updated item
              return item;
            })
          );

          // Set the page content to the updated data with feed data
          updated.data.content.content = translatedData;

          // Set the data, if any updates where applied they will also be set
          setData(updated);
        }
        */
      } catch (err) {
        console.error('PERFORM ERROR:', err);
      }
    }

    // Start fetch and guard against unhandled rejections
    perform().catch((err) => console.error('perform() unhandled error:', err));

    // Cleanup to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [complex, params, url, skip]);

  return data;
}

export { useFetchPostComplex };
